import React, { useState, useRef, useEffect } from "react";
import { COUNTRIES, findCountryByDialCode } from "./countries";

// Cross-platform Flag Component (solves Windows lack of emoji flags)
const CountryFlag = ({ code, name, width = 20, height = 14 }) => {
  const [hasError, setHasError] = useState(false);
  const lowerCode = code?.toLowerCase();

  if (hasError || !lowerCode) {
    return (
      <span
        style={{
          fontSize: "11px",
          fontWeight: "700",
          color: "#475569",
          background: "#e2e8f0",
          padding: "2px 4px",
          borderRadius: "3px",
          lineHeight: 1,
        }}
      >
        {code}
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/w40/${lowerCode}.png`}
      width={width}
      height={height}
      alt={name || code}
      loading="lazy"
      onError={() => setHasError(true)}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: "2px",
        objectFit: "cover",
        display: "inline-block",
        verticalAlign: "middle",
        boxShadow: "0 0 1px rgba(0, 0, 0, 0.4)",
        flexShrink: 0,
      }}
    />
  );
};

const CountryCodeSelect = ({
  value = "+1",
  onChange,
  disabled = false,
  style = {},
  buttonStyle = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Identify selected country
  const selectedCountry = findCountryByDialCode(value);

  // Close when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Filter countries
  const filteredCountries = COUNTRIES.filter((country) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    return (
      country.name.toLowerCase().includes(term) ||
      country.dial_code.toLowerCase().includes(term) ||
      country.code.toLowerCase().includes(term)
    );
  });

  const handleSelect = (country) => {
    if (onChange) {
      onChange(country.dial_code, country);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
        display: "inline-block",
        flexShrink: 0,
        ...style,
      }}
    >
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          height: "100%",
          minHeight: "44px",
          padding: "8px 12px",
          background: "#ffffff",
          border: isOpen ? "1px solid #3b82f6" : "1px solid #cbd5e1",
          borderRadius: "8px",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: "14px",
          fontWeight: "500",
          color: "#1e293b",
          outline: "none",
          transition: "all 0.2s ease",
          boxShadow: isOpen ? "0 0 0 2px rgba(59, 130, 246, 0.15)" : "none",
          boxSizing: "border-box",
          whiteSpace: "nowrap",
          userSelect: "none",
          ...buttonStyle,
        }}
      >
        <CountryFlag
          code={selectedCountry.code}
          name={selectedCountry.name}
          width={22}
          height={15}
        />
        <span style={{ fontSize: "14px", color: "#334155", fontWeight: "600" }}>
          {selectedCountry.dial_code}
        </span>
        <span
          style={{
            fontSize: "10px",
            color: "#64748b",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            display: "inline-block",
            marginLeft: "2px",
          }}
        >
          ▼
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 1050,
            width: "300px",
            maxHeight: "310px",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            textAlign: "left",
          }}
        >
          {/* Search Box */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f1f5f9",
              backgroundColor: "#f8fafc",
            }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search country or code..."
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: "13px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                outline: "none",
                boxSizing: "border-box",
                marginBottom: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Country List */}
          <div
            style={{
              overflowY: "auto",
              maxHeight: "250px",
              padding: "4px 0",
            }}
          >
            {filteredCountries.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  fontSize: "13px",
                  color: "#94a3b8",
                  textAlign: "center",
                }}
              >
                No countries found
              </div>
            ) : (
              filteredCountries.map((country) => {
                const isSelected = country.dial_code === value;
                return (
                  <div
                    key={`${country.code}-${country.dial_code}`}
                    onClick={() => handleSelect(country)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#eff6ff" : "transparent",
                      transition: "background-color 0.15s",
                      fontSize: "13px",
                      color: isSelected ? "#1d4ed8" : "#334155",
                      fontWeight: isSelected ? "600" : "normal",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <CountryFlag
                        code={country.code}
                        name={country.name}
                        width={20}
                        height={14}
                      />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {country.name}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "12px",
                        color: isSelected ? "#2563eb" : "#64748b",
                        fontWeight: "600",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {country.dial_code}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
